import { BusinessDayCalculatorPL, ClientRepositoryBase, CommunityTransactionForPurchaseQuery, CompanyParametersQuery, ContextKeys, CurrencyCollectionQuery, DocumentExchangeRateValidationBoPart, DocumentItemsNumberingBoPart, DocumentNumberBoPart, ExchangeRateMixin, fillDocumentContractorBasedOnContractorId, KsefNumberValidator, NumberingQuery, PaymentMethodQuery, ProductsGetAssociatedEInvoiceSymbolsQuery, propertyChangedJoinPointFilter, PurchaseKindQuery, rewriteDocumentContractorWithFreshDataAsync, rewriteDocumentItem, rewriteExchangeRate, ToPayMixin, UnitQuery, VatRateForPurchaseQuery } from '@skylab-shared/business';
import { BO, BusinessObjectBase, PropertyChangedJoinPoint, QueryBus, Weaver } from '@skylab-shared/core';
import { CalculationMethod, DocumentClientModel, DocumentItemModel, DocumentPaymentListItemModel, ExchangeRateModel, InventoryCostCalculationMethod, InventoryEffect, InventoryGoodsReceiptAdjustmentDocumentModel, InventoryGoodsReceiptDocumentModel, ProductModel, PurchaseDocumentKind, PurchaseEInvoiceDocumentModel, PurchaseInvoiceDocumentItemModel, PurchaseInvoiceDocumentModel, RelatedEntity, SplitPaymentState, VatTableItemModel } from '@skylab-shared/dataModel';
import { dateDiffInDays, head, substractDays, unique } from '@skylab-shared/utils';
import { ProductRepository } from '../product/product.repo';
import { PurchaseLinksMixin } from './mixins/purchase-links.mixin';
import { PurchaseDocumentCalculatorBoPart } from './parts/purchase-document-calculator.bo-part';
import { PurchaseDocumentJpkGroupsBOPart } from './parts/purchase-document-jpk-groups.bo-part';
import { PurchaseDocumentPaymentsBOPart } from './parts/purchase-document-payments.bo-part';
import { PurchaseDocumentValidationBOPart } from './parts/purchase-document-validation.bo-part';
import { PurchaseDocumentVatDeductionDateBOPart } from './parts/purchase-document-vat-deduction-date.bo-part';
import { PurchaseInvoiceIssuedToInventoryGoodsReceiptBoPart } from './parts/purchase-invoice-issued-to-inventory-goods-receipt.bo-part';
import { PurchaseDocumentItemsValidationsBoPart } from './parts/purchase-invoice-items-validations.bo-part';
import { PurchaseInvoiceVatTableValidationsBoPart } from './parts/purchase-invoice-vat-table-validations.bo-part';
import { PurchaseKsefNumberAlreadyInUseQuery } from './queries/purchase-ksef-number-already-in-use.query';
import { getPurchaseInvoiceItemVatRateId, refreshPurchaseInvoiceDocumentItemPriceAsync } from './utils/purchase-document-item-utils';
import { rewriteAddressFromParsedEInvoiceAddress } from './utils/purchase-rewriting-utils';
import { PurchaseKsefNumberAlreadyInUseError } from './violations/purchase-ksef-number-already-in-use-error';
import { PurchaseKsefNumberInvalidError } from './violations/purchase-ksef-number-invalid-error';
import { PurchaseWrongInventoryEffectForManualCalculation } from './violations/purchase-wrong-inventory-effect-for-manual-calculation-error';

export interface PurchaseInvoiceDocumentBO extends PurchaseLinksMixin<PurchaseInvoiceDocumentModel>, ToPayMixin<PurchaseInvoiceDocumentModel>, ExchangeRateMixin<PurchaseInvoiceDocumentModel> {};

@BO({
    modelType: PurchaseInvoiceDocumentModel,
    mixins: [PurchaseLinksMixin, ExchangeRateMixin, ToPayMixin]
})
export class PurchaseInvoiceDocumentBO extends BusinessObjectBase<PurchaseInvoiceDocumentModel> {
    public constructor(
        weaver: Weaver,
        protected readonly queryBus: QueryBus,
        protected readonly productRepository: ProductRepository,
        protected readonly clientRepository: ClientRepositoryBase
    ) {
        super(weaver);
        this.parts.push(new DocumentNumberBoPart(weaver, queryBus));
        this.parts.push(new DocumentExchangeRateValidationBoPart(weaver));
        this.parts.push(new PurchaseDocumentValidationBOPart(weaver, queryBus));
        this.parts.push(new PurchaseDocumentPaymentsBOPart(weaver));
        this.parts.push(new PurchaseDocumentJpkGroupsBOPart(weaver));
        this.parts.push(new PurchaseDocumentCalculatorBoPart(weaver));
        this.parts.push(new DocumentItemsNumberingBoPart(weaver));
        this.parts.push(new PurchaseDocumentItemsValidationsBoPart(weaver));
        this.parts.push(new PurchaseInvoiceIssuedToInventoryGoodsReceiptBoPart(weaver));
        this.parts.push(new PurchaseInvoiceVatTableValidationsBoPart(weaver, () => this.isDocumentAdjusted));
        this.parts.push(new PurchaseDocumentVatDeductionDateBOPart(weaver));
    }

    protected override async initializeAsync(): Promise<void> {
        await super.initializeAsync();

        await Promise.all([
            this.localContext.preload(VatRateForPurchaseQuery),
            this.localContext.preload(CurrencyCollectionQuery),
            this.localContext.preload(CompanyParametersQuery),
            this.localContext.preload(PurchaseKindQuery),
            this.localContext.preload(UnitQuery),
            this.localContext.preload(CommunityTransactionForPurchaseQuery),
            this.localContext.preload(PaymentMethodQuery),
            this.localContext.preload(NumberingQuery),
            this.localContext.preload(ContextKeys.isInvoiceToInventoryGoodsReceipt(this.model.sun), () => Promise.resolve(this.invoiceIssuedToInventoryGoodsReceipt)),
            this.localContext.preload(ContextKeys.relatedGoodsReceiptDocumentToInvoice(this.model.sun), () => Promise.resolve(this.internalRelatedGoodsReceiptDocument))
        ]);

        if (this.model.isPersistent) {
            await this.loadLinksAsync();

            if (this.linksInvoiceIssuedToInventoryGoodsReceipt)
                await this.setInvoiceIssuedToInventoryGoodsReceiptAsync(this.linksInvoiceIssuedToInventoryGoodsReceipt);
        }

        this.registerDocumentReadonlyRules();
        this.registerDocumentFromEInvoiceRules();
    }

    private registerDocumentFromEInvoiceRules(): void {
        const modelType = PurchaseInvoiceDocumentModel;
        const omitValidateKsefNumber = () => !this.model.ksefDocumentId || this.model.ksefHeaderId || !!this.internalRelatedEInvoiceDocument
        const ksefNumberValid = () => KsefNumberValidator.isValid(this.model.ksefDocumentId!);

        this.readonlyChecker.addOmitRule([
            [modelType, 'automaticVatCalculation'],
        ], () => true);

        this.readonlyChecker.addOmitRule([
            [PurchaseInvoiceDocumentItemModel, 'productName'],
            [PurchaseInvoiceDocumentItemModel, 'symbol'],
        ], () => true);

        this.validation.addRule(
            [[modelType, 'ksefDocumentId']],
            _ => {
                if (omitValidateKsefNumber()) return true;
                return ksefNumberValid();
            },
            PurchaseKsefNumberInvalidError
        );

        this.validation.addRule(
            [[modelType, 'inventoryEffect'], [modelType, 'calculationMethod']],
            [[modelType, 'inventoryEffect']],
            _ => {
                return (this.model.calculationMethod !== CalculationMethod.Manual) || (this.model.inventoryEffect !== InventoryEffect.Realized);
            },
            PurchaseWrongInventoryEffectForManualCalculation
        );

        this.validation.addRule(
            [[modelType, 'ksefDocumentId']],
            async _ => {
                if (omitValidateKsefNumber() || !ksefNumberValid()) return true;
                const result = await this.queryBus.queryAsync(new PurchaseKsefNumberAlreadyInUseQuery({ksefDocumentId: this.model.ksefDocumentId!}));
                if (!result.documentId) return true;
                if (this.model.isPersistent && (this.model.id === result.documentId)) return true;

                return !result.documentId;
            },
            PurchaseKsefNumberAlreadyInUseError
        );

        this.readonly.addRule([
            [modelType, 'ksefDocumentId'],
            [modelType, 'ksefSubmissionMode'],
            [modelType, 'ksefCollectiveIdentifier'],
        ], () => !!this.internalRelatedEInvoiceDocument || !!this.model.ksefHeaderId);

        this.readonly.addRule([
            [modelType, 'automaticVatCalculation'],
        ], () => this.model.calculationMethod === CalculationMethod.Manual);

        this.readonly.addRule([
            [PurchaseInvoiceDocumentItemModel, 'symbol'],
        ], ctx => !!ctx.target.originalEInvoiceSymbol);
    }

    private registerDocumentReadonlyRules(): void {
        const modelType = PurchaseInvoiceDocumentModel;

        this.readonly.setReadonlyOnSubtree(PurchaseInvoiceDocumentItemModel, () => this.isDocumentAdjusted, {
            exceptions: [
                [PurchaseInvoiceDocumentItemModel, <keyof PurchaseInvoiceDocumentItemModel>'note']
            ]
        });

        this.readonly.setReadonlyOnSubtree(ExchangeRateModel, () => this.isDocumentAdjusted);

        this.readonly.addRule([
            [modelType, 'costCalculationMethod'],
            [modelType, 'currencyId'],
            [modelType, 'calculationMethod'],
            [modelType, 'documentNumber'],
            [modelType, 'issueDate'],
            [modelType, 'seller'],
            [modelType, 'issuer'],
            [modelType, 'numberingId'],
            [modelType, 'automaticVatCalculation'],
            [modelType, 'receiptDate'],
            [modelType, 'bankAccount'],
            [modelType, 'recipient'],
            [modelType, 'communityTransactionId'],
            [modelType, 'jpkVatGroups'],
            [modelType, 'transactionLocation'],
            [modelType, 'externalDocumentNumber'],
            [modelType, 'externalDocumentNumber'],
            [modelType, 'paymentMethodId'],
            [modelType, 'dueDate'],
            [modelType, 'dueDateDays'],
            [modelType, 'cashBasis'],
            [modelType, 'splitPayment'],
            [modelType, 'fixedAsset'],
            [modelType, 'deliveryDate'],
            [modelType, 'ksefDocumentId'],
            [modelType, 'ksefCollectiveIdentifier'],
            [modelType, 'ksefSubmissionMode'],
        ], () => this.isDocumentAdjusted);
    }

    protected calculateDateForCurrencyExchangeRate(): Date {
        const date = substractDays(this.model.vatDeductionDate || this.model.receiptDate, 1);
        return BusinessDayCalculatorPL.getLastBusinessDay(date);
    }

    public async refreshDocumentItemsPricesAsync(): Promise<void> {
        await Promise.all(this.model.items.map(async item => {
            const product = item.productId ? await this.localContext.getAsync(ContextKeys.product(item.productId), () => this.productRepository.findAsync(item.productId!)) : new ProductModel();
            await refreshPurchaseInvoiceDocumentItemPriceAsync(item, this.localContext, this.model, product);
        }));
    }

    private registerChangeLinksFromEInvoiceAfterSourceClientIdChanged() {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(DocumentClientModel, ['sourceClientId'])(
            (ctx) => {
                if ((ctx.model === this.model.seller) && this.model.seller?.sourceClientId) {
                    this.handleItemsLinkingToProductAsync();
                }
            }
        )));
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(PurchaseInvoiceDocumentModel, ['seller'])(
            (ctx) => {
                if (!this.model.seller?.sourceClientId) {
                    this.model.items.filter(e => !!e.originalEInvoiceSymbol).forEach(e => {
                        e.productId = null;
                        e.includesInventoryControl = false;
                        e.symbol = e.originalEInvoiceSymbol;
                    })
                }
            }
        )));
    }

    public async fillPurchaseInvoiceToGoodsReceiptAsync(source: InventoryGoodsReceiptDocumentModel | InventoryGoodsReceiptAdjustmentDocumentModel, relatedDocuments: RelatedEntity[]): Promise<void> {
        await this.setInvoiceIssuedToInventoryGoodsReceiptAsync(true);
        await this.setRelatedGoodsReceiptDocument(source);

        const documentConfig = this.localContext.firstPreloaded(PurchaseKindQuery, { name: PurchaseDocumentKind.PurchaseInvoice });
        const companyParameters = this.localContext.getPreloaded(CompanyParametersQuery);
        const defaultCommunityTransaction = this.localContext.firstPreloaded(CommunityTransactionForPurchaseQuery, { default: true });
        const numberings = this.localContext.getPreloaded(NumberingQuery);

        this.model.automaticVatCalculation = true;

        this.model.relatedInventoryDocuments = [...relatedDocuments];
        this.model.inventoryEffect = InventoryEffect.Realized;

        this.model.issuedByVatPayer = companyParameters.vatPayer;

        await rewriteDocumentContractorWithFreshDataAsync(this.model, source, this.clientRepository);

        this.model.calculationMethod = source.calculationMethod;
        this.model.costCalculationMethod = source.costCalculationMethod;

        this.model.communityTransactionId = defaultCommunityTransaction.id;
        this.model.currencyId = source.currencyId;

        const [{ id: defaultNumberingId }] = numberings.filter(n => n.default && n.kinds.includes(this.model.kind));
        this.model.numberingId = defaultNumberingId;

        this.model.deliveryDate = source.receiptDate;
        this.model.issueDate = new Date();
        this.model.issueDate = new Date();
        this.model.receiptDate = new Date();
        if (this.model.issuedByVatPayer) this.model.vatDeductionDate = new Date();

        this.model.transactionLocation = source.transactionLocation;
        this.model.recipient = source.recipient;
        this.model.issuer = source.issuer;

        this.model.splitPayment = SplitPaymentState.False;
        this.model.paymentMethodId = documentConfig.defaultPaymentMethodId;

        source.items.forEach(item => {
            const model = new PurchaseInvoiceDocumentItemModel();
            this.model.items.push(model);
            rewriteDocumentItem(model, item);
            model.vatRateId = item.vatRateId;
            model.relatedInventoryItemsIds = item.id ? [item.id] : [];
            model.includesInventoryControl = true;
        });

        if (source.exchangeRate) {
            const exchangeRate = new ExchangeRateModel();
            this.model.exchangeRate = exchangeRate;
            rewriteExchangeRate(exchangeRate, source.exchangeRate);
        }
    }

    public async fillPurchaseInvoiceToEPurchaseInvoice(source: PurchaseEInvoiceDocumentModel, sellerId: number | null): Promise<void> {
        this.setInternalRelatedEInvoiceDocument(source);
        const numberings = this.localContext.getPreloaded(NumberingQuery);
        const paymentMethods = this.localContext.getPreloaded(PaymentMethodQuery);
        const { vatPayer, systemCurrency } = this.localContext.getPreloaded(CompanyParametersQuery);
        const { id: systemCurrencyId } = this.localContext.firstPreloaded(CurrencyCollectionQuery, {symbol: systemCurrency});
        const units = this.localContext.getPreloaded(UnitQuery);

        this.model.originalEInvoiceDocumentId = source.id;
        this.model.ksefHeaderId = source.ksefHeaderId;
        this.model.ksefHash = source.ksefHash;
        this.model.ksefCollectiveIdentifier = source.ksefCollectiveIdentifier;
        this.model.ksefDocumentId = source.ksefDocumentId;
        this.model.ksefSubmissionMode = source.ksefSubmissionMode;
        this.model.dateOfAssigningKsefNumber = source.dateOfAssigningKsefNumber;

        this.model.issuedByVatPayer = source.issuedByVatPayer;

        if (!sellerId) {
            await rewriteDocumentContractorWithFreshDataAsync(this.model, source, this.clientRepository);
            rewriteAddressFromParsedEInvoiceAddress(this.model.seller?.address, source.seller?.address);
        } else {
            await fillDocumentContractorBasedOnContractorId(this.model, this.clientRepository, sellerId);
        }

        this.model.calculationMethod = source.calculationMethod as CalculationMethod.Gross | CalculationMethod.Net | CalculationMethod.Manual;
        this.model.cashBasis = source.cashBasis;
        this.model.fixedAsset = source.fixedAsset;

        this.model.costCalculationMethod = vatPayer ? InventoryCostCalculationMethod.Net : InventoryCostCalculationMethod.Gross;

        this.model.communityTransactionId = source.communityTransactionId
        this.model.currencyId = source.currencyId;
        this.model.bankAccount = source.bankAccount;

        source.payments.forEach(sourcePayment => {
            const model = new DocumentPaymentListItemModel();
            model.date = sourcePayment.date;
            model.onDocument = sourcePayment.onDocument;
            model.value = sourcePayment.value;
            this.model.payments.push(model);
        });
        this.model.externalDocumentNumber = source.externalDocumentNumber;
        const [{ id: defaultNumberingId }] = numberings.filter(n => n.default && n.kinds.includes(this.model.kind));
        this.model.numberingId = defaultNumberingId;

        this.model.deliveryDate = source.deliveryDate || source.issueDate;
        this.model.issueDate = source.issueDate;
        this.model.receiptDate = source.receiptDate;
        this.model.vatDeductionDate = source.vatDeductionDate;

        this.model.transactionLocation = source.transactionLocation;
        this.model.recipient = source.recipient;
        this.model.issuer = source.issuer;
        this.model.jpkVatGroups = source.jpkVatGroups;
        this.model.splitPayment = source.splitPayment;
        this.model.note = source.note;

        if (paymentMethods.find(e => e.id === source.paymentMethodId)) {
            this.model.paymentMethodId = source.paymentMethodId;
        }

        this.model.dueDate = source.dueDate || head(this.model.payments?.asArray() || [])?.date || this.model.issueDate
        this.model.dueDateDays = this.model.issueDate ? dateDiffInDays(this.model.dueDate, this.model.issueDate) : null;

        source.items.forEach(sourceItem => {
            const model = new PurchaseInvoiceDocumentItemModel();
            this.model.items.push(model);
            this.setDefaultItemValuesFromEInvoiceDocument(sourceItem);
            rewriteDocumentItem(model, sourceItem);
            if (this.model.calculationMethod === CalculationMethod.Manual) {
                model.price_grossAfterDiscount = sourceItem.price_grossAfterDiscount;
                model.price_netAfterDiscount = sourceItem.price_netAfterDiscount;
                model.value_grossAfterDiscount = sourceItem.value_grossAfterDiscount;
                model.value_grossBeforeDiscount = sourceItem.value_grossBeforeDiscount;
                model.value_netAfterDiscount = sourceItem.value_netAfterDiscount;
                model.value_netBeforeDiscount = sourceItem.value_netBeforeDiscount;
                model.value_vatAfterDiscount = sourceItem.value_vatAfterDiscount;
                model.value_vatBeforeDiscount = sourceItem.value_vatBeforeDiscount;
            }
            model.relatedInventoryItemsIds = [];
            model.originalEInvoiceSymbol = sourceItem.symbol;
            model.itemFromEInvoice = true;
            if (!model.unitId && sourceItem.unitSymbol) {
                const unit = units.find(e => e.symbol === sourceItem.unitSymbol)
                if (unit) model.unitId = unit.id;
            }
        });

        await this.handleItemsLinkingToProductAsync();

        if (source.exchangeRate) {
            const exchangeRate = new ExchangeRateModel();
            this.model.exchangeRate = exchangeRate;
            rewriteExchangeRate(exchangeRate, source.exchangeRate);
        }

        this.model.automaticVatCalculation = this.model.calculationMethod !== CalculationMethod.Manual;

        const sourceVatTable = source.calculationMethod === CalculationMethod.Manual
            ? systemCurrencyId === source.currencyId
                ? source.vatTableItems
                : source.vatTableItemsInBaseCurrency
            : [];

        if (!this.model.automaticVatCalculation) {
            this.model.grossValue = source.grossValue;
            this.model.currencyGrossValue = source.currencyGrossValue;
            this.model.netValue = source.netValue;
            this.model.vatTableItems.push(...(sourceVatTable || []).map((sourceVatItem: VatTableItemModel) => {
                return new VatTableItemModel({
                    grossValue: sourceVatItem.grossValue,
                    netValue: sourceVatItem.netValue,
                    vatRateId: sourceVatItem.vatRateId,
                    vatValue: sourceVatItem.vatValue,
                    cost: sourceVatItem.cost
                });
            }));
        }

        this.registerChangeLinksFromEInvoiceAfterSourceClientIdChanged();
    }

    private async handleItemsLinkingToProductAsync(): Promise<void> {
        if (!this.model.seller?.sourceClientId) return;
        const symbols = unique(this.model.items.filter(e => !!e.originalEInvoiceSymbol).map(e => e.originalEInvoiceSymbol));
        if (!symbols?.length) return;
        const result = await this.queryBus.queryAsync(new ProductsGetAssociatedEInvoiceSymbolsQuery({clientId: this.model.seller.sourceClientId, eInvoiceItemsSymbols: symbols}));
        result.forEach(matchedProduct => {
            this.model.items.forEach(item => {
                if (item.originalEInvoiceSymbol === matchedProduct.eInvoiceSymbol) {
                    item.productName = matchedProduct.productName;
                    item.symbol = matchedProduct.symbol;
                    item.productId = matchedProduct.productId;
                    item.vatRateId = getPurchaseInvoiceItemVatRateId(this.localContext, this.model, matchedProduct);
                    item.unitId = matchedProduct.unitId;
                    item.includesInventoryControl = matchedProduct.includesInventoryControl;
                }
            });
        });
    }

    private internalRelatedEInvoiceDocument: PurchaseEInvoiceDocumentModel;
    private async setInternalRelatedEInvoiceDocument(value: PurchaseEInvoiceDocumentModel) {
        this.internalRelatedEInvoiceDocument = value;
    }

    public getRelatedEInvoiceDocument(): PurchaseEInvoiceDocumentModel {
        return this.internalRelatedEInvoiceDocument;
    }

    private internalInvoiceIssuedToInventoryGoodsReceipt = false;
    public get invoiceIssuedToInventoryGoodsReceipt(): boolean {
        return this.internalInvoiceIssuedToInventoryGoodsReceipt;
    }

    private async setInvoiceIssuedToInventoryGoodsReceiptAsync(value: boolean) {
        this.internalInvoiceIssuedToInventoryGoodsReceipt = value;
        await this.localContext.refresh(ContextKeys.isInvoiceToInventoryGoodsReceipt(this.model.sun));
    }

    private internalRelatedGoodsReceiptDocument: InventoryGoodsReceiptDocumentModel;
    private async setRelatedGoodsReceiptDocument(value: InventoryGoodsReceiptDocumentModel) {
        this.internalRelatedGoodsReceiptDocument = value;
        await this.localContext.refresh(ContextKeys.relatedGoodsReceiptDocumentToInvoice(this.model.sun));
    }

    private setDefaultItemValuesFromEInvoiceDocument(source: DocumentItemModel): void {
        const targetProperties = [
            'price_grossAfterDiscount', 'price_grossBeforeDiscount', 'price_netAfterDiscount', 'price_netBeforeDiscount', 'unitId', 'vatRateId',
            'value_grossAfterDiscount', 'value_grossBeforeDiscount', 'value_netAfterDiscount', 'value_netBeforeDiscount', 'value_vatAfterDiscount', 'value_vatBeforeDiscount'
        ];
        targetProperties.forEach(e => {
            if (!(source as any)[e]) (source as any)[e] = 0;
        });
    }
}